import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import usePageMeta from "@/hooks/usePageMeta";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ListYourSchool() {
  usePageMeta("List Your School", "Get your school listed on India's fastest-growing school directory. Free and takes under 2 minutes.");
  const [f, setF] = useState({ school_name: "", city: "", board: "", school_type: "", contact_name: "", contact_email: "", contact_phone: "", website: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/submissions", f);
      setSent(true);
      toast.success("Submission received! Our team will reach out soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />
      <div className="mx-auto max-w-xl px-6 py-24 text-center flex-1">
        <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
        <h1 className="text-3xl font-bold mt-6" data-testid="submission-success">Thanks — we received your details</h1>
        <p className="text-slate-500 mt-3">Our team will reach out within 2 business days to verify and onboard your school.</p>
      </div>
      <PublicFooter />
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />
      <div className="mx-auto max-w-3xl px-6 py-12 flex-1">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="list-school-heading">List Your School</h1>
        <p className="text-slate-500 mt-2">Get your school in front of thousands of parents. Fill this form and we'll onboard you.</p>
        <form onSubmit={submit} className="mt-8 grid md:grid-cols-2 gap-4 bg-white p-6 rounded-lg border border-slate-200" data-testid="list-school-form">
          {[
            ["school_name", "School Name *", "text", true],
            ["city", "City *", "text", true],
            ["contact_name", "Contact Name *", "text", true],
            ["contact_email", "Contact Email *", "email", true],
            ["contact_phone", "Contact Phone *", "tel", true],
            ["website", "Website (optional)", "url", false],
          ].map(([k, l, t, req]) => (
            <div key={k}>
              <Label className="mb-2 block">{l}</Label>
              <Input required={req} type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ls-${k}`} />
            </div>
          ))}
          <div>
            <Label className="mb-2 block">Board</Label>
            <Select value={f.board} onValueChange={(v) => setF({ ...f, board: v })}>
              <SelectTrigger data-testid="ls-board"><SelectValue placeholder="Board" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CBSE">CBSE</SelectItem>
                <SelectItem value="ICSE">ICSE</SelectItem>
                <SelectItem value="State">State</SelectItem>
                <SelectItem value="IB">IB</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-2 block">School Type</Label>
            <Select value={f.school_type} onValueChange={(v) => setF({ ...f, school_type: v })}>
              <SelectTrigger data-testid="ls-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pre">Pre-Primary</SelectItem>
                <SelectItem value="Primary">Primary</SelectItem>
                <SelectItem value="Middle">Middle</SelectItem>
                <SelectItem value="Secondary">Secondary</SelectItem>
                <SelectItem value="Sr. Secondary">Sr. Secondary</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="mb-2 block">Message</Label>
            <Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ls-message" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="ls-submit">Submit</Button>
          </div>
        </form>
      </div>
      <PublicFooter />
    </div>
  );
}
